import { motion } from 'framer-motion';
import {
  LockClosedIcon,
  UserIcon,
  DevicePhoneMobileIcon,
} from '@heroicons/react/24/outline';
import type { ComponentType, SVGProps } from 'react';

interface FeatureTile {
  icon: ComponentType<SVGProps<SVGSVGElement>>;
  title: string;
  description: string;
}

const FEATURE_TILES: FeatureTile[] = [
  {
    icon: LockClosedIcon,
    title: 'No ads, no data sold',
    description:
      'Your entries are never used for advertising or sold to third parties.',
  },
  {
    icon: UserIcon,
    title: 'Anonymous by design',
    description:
      'We never ask for your last name. Fellowship anonymity is foundational.',
  },
  {
    icon: DevicePhoneMobileIcon,
    title: 'Works offline',
    description:
      'Install as an app on any device, check in without internet.',
  },
];

const containerVariants = {
  hidden: {},
  visible: {
    transition: {
      staggerChildren: 0.12,
    },
  },
};

const tileVariants = {
  hidden: { opacity: 0, y: 24 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
};

export function FreeAndPrivateSection() {
  return (
    <section
      id="features"
      className="bg-[#1B263B] py-20 px-4"
    >
      <div className="max-w-6xl mx-auto">
        {/* Heading */}
        <motion.div
          className="text-center mb-14"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: '-80px' }}
          transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] as const }}
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Your recovery is yours.
          </h2>
          <p className="text-lg text-slate-300 max-w-2xl mx-auto leading-relaxed">
            Core recovery tools are free forever. Optional AI-powered insights
            available with Premium.
          </p>
        </motion.div>

        {/* Feature tiles */}
        <motion.div
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-60px' }}
        >
          {FEATURE_TILES.map((tile) => {
            const Icon = tile.icon;
            return (
              <motion.div
                key={tile.title}
                variants={tileVariants}
                className="rounded-2xl bg-white/5 border border-white/10 p-8 flex flex-col items-start gap-4"
              >
                <div className="p-3 rounded-xl bg-teal-500/15">
                  <Icon className="w-6 h-6 text-teal-400" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-white mb-2">
                    {tile.title}
                  </h3>
                  <p className="text-slate-400 text-sm leading-relaxed">
                    {tile.description}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
